import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card } from '../../../components/ui/Card';
import { formatCurrency } from '../../../lib/utils';

interface SalesData {
  fecha: string;
  ventas?: number;
  total?: number; 
  devoluciones?: number;
  ganancia?: number;
}

interface SalesChartProps {
  data: SalesData[];
  isLoading?: boolean;
  periodo: '7d' | '15d' | '30d' | '90d';
  onPeriodoChange: (p: '7d' | '15d' | '30d' | '90d') => void;
}

const formatYAxis = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value}`;
};

export function SalesChart({ data, isLoading = false, periodo, onPeriodoChange }: SalesChartProps) {
  const [tipoGrafico, setTipoGrafico] = useState<'linea' | 'barra'>('linea');

  // Forzar barras en móvil
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setTipoGrafico('barra');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando datos...</div>
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">No hay datos disponibles</div>
        </div>
      </Card>
    );
  }

  const formattedData = data.map((d) => {
    let fechaFormateada = d.fecha;
    try {
      const dateObj = new Date(d.fecha);
      fechaFormateada = new Intl.DateTimeFormat('es-ES', { 
        day: '2-digit', 
        month: 'short',
        timeZone: 'UTC'
      }).format(dateObj);
    } catch (e) {
    }

    return {
      ...d,
      fechaStr: fechaFormateada,
      ventas: d.ventas || d.total || 0,
      devoluciones: d.devoluciones || 0,
      ganancia: d.ganancia || 0,
    };
  });

  const totalVentas = formattedData.reduce((sum, d) => sum + d.ventas, 0);
  const totalDevoluciones = formattedData.reduce((sum, d) => sum + d.devoluciones, 0);
  const totalGanancia = formattedData.reduce((sum, d) => sum + d.ganancia, 0);

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800"> Ventas</h3>
          <p className="text-sm text-gray-500">Evolución de ventas en el tiempo</p>
        </div>
        <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
          <div className="flex gap-1">
            {(['7d', '15d', '30d', '90d'] as const).map((p) => (
              <button
                key={p}
                onClick={() => onPeriodoChange(p)}
                className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                  periodo === p
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p === '7d' ? '7 días' : p === '15d' ? '15 días' : p === '30d' ? '30 días' : '90 días'}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {/* Botón de Línea - Oculto en móvil */}
            <button
              onClick={() => setTipoGrafico('linea')}
              className={`hidden sm:inline-block px-3 py-1 text-xs rounded-lg transition-colors ${
                tipoGrafico === 'linea'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Línea
            </button>
            {/* Botón de Barras - Siempre visible */}
            <button
              onClick={() => setTipoGrafico('barra')}
              className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                tipoGrafico === 'barra'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Barras
            </button>
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-gray-500">Total Ventas</p>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(totalVentas)}</p>
        </div>
        <div className="p-3 bg-red-50 rounded-lg">
          <p className="text-xs text-gray-500">Devoluciones</p>
          <p className="text-lg font-bold text-red-600">{formatCurrency(totalDevoluciones)}</p>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <p className="text-xs text-gray-500">Ganancia Estimada</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(totalGanancia)}</p>
        </div>
      </div>

      {/* Gráfico */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {tipoGrafico === 'linea' ? (
            <LineChart data={formattedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fechaStr" />
              <YAxis width={65} tickFormatter={formatYAxis} />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Legend />
              <Line
                type="monotone"
                dataKey="ventas"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Ventas"
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="devoluciones"
                stroke="#ef4444"
                strokeWidth={2}
                name="Devoluciones"
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="ganancia"
                stroke="#22c55e"
                strokeWidth={2}
                name="Ganancia"
                dot={{ r: 4 }}
              />
            </LineChart>
          ) : (
            <BarChart data={formattedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fechaStr" />
              <YAxis width={65} tickFormatter={formatYAxis} />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Legend />
              <Bar dataKey="ventas" fill="#3b82f6" name="Ventas" />
              <Bar dataKey="devoluciones" fill="#ef4444" name="Devoluciones" />
              <Bar dataKey="ganancia" fill="#22c55e" name="Ganancia" />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}